import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LevelCircle from './LevelCircle'; 

// This component displays the summary of a round or event, including XP progress, scores, and leaderboard.
function BetweenRounds({ type, currentRoundData, eventStats, nextCourse, onStartNextRound, scores, eventId }) {
  const navigate = useNavigate();
  const [xpData, setXpData] = useState(null);
  const [displayedXP, setDisplayedXP] = useState(0);
  const [previousLevel, setPreviousLevel] = useState(null);
  const [hasLeveledUp, setHasLeveledUp] = useState(false);
  const [animatedTotalXp, setAnimatedTotalXp] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);

  // Constants for animation
  const ANIMATION_DURATION = 2400;
  const FRAME_RATE = 60;
  const TOTAL_FRAMES = Math.round((ANIMATION_DURATION / 1000) * FRAME_RATE);

  // Fetch the leaderboard data from the server
  const fetchLeaderboard = () => {
    setLeaderboardLoading(true);
    fetch('/server/crazy-cup-server/getEventLeaderBoard.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({eventId}),
    })
      .then((res) => res.json())
      .then((data) => setLeaderboard(data.data || []))
      .finally(() => setLeaderboardLoading(false));
  };

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch XP data and handle leveling up
  useEffect(() => {
    const clerkUserId = localStorage.getItem('userId');
    if (clerkUserId && scores) {
      fetch('/server/crazy-cup-server/addXP.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clerk_user_id: clerkUserId, scores }),
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.success) {
            if (previousLevel !== null && data.data.level > previousLevel) {
              setHasLeveledUp(true); 
            }
            setPreviousLevel(data.data.level);
            setXpData(data.data);
          } else {
            console.error('Error adding XP:', data.message);
          }
        })
        .catch((error) => console.error('Error calling addXP API:', error));
    }
  }, [scores, previousLevel]);

  // Animate XP progress
  useEffect(() => {
    if (xpData) {
      let frame = 0;
      const startXP = 0;
      const endXP = xpData.xp;
      const interval = setInterval(() => {
        frame++;
        const progress = frame / TOTAL_FRAMES;
        const easedProgress = progress * (2 - progress);
        const currentValue = Math.round(startXP + (endXP - startXP) * easedProgress);
        setDisplayedXP(currentValue);
        if (frame === TOTAL_FRAMES) {
          setDisplayedXP(endXP);
          clearInterval(interval);
        }
      }, 1000 / FRAME_RATE);
      return () => clearInterval(interval);
    }
  }, [xpData]);

  // Calculate total XP earned based on scores
  const scoreCounts = {
    par: 0,
    birdie: 0,
    eagle: 0,
    albatross: 0,
    holeInOne: 0,
  };

  // XP values for each score type
  const xpValues = {
    par: 16,
    birdie: 21,
    eagle: 33,
    albatross: 54,
    holeInOne: 109,
  };

  // Calculate scores from the current round data
  if (currentRoundData && currentRoundData.scores) {
    currentRoundData.scores.forEach(({ par, shots }) => {
      if (shots === 1) {
        scoreCounts.holeInOne += 1;
      } else if (shots === par - 3) {
        scoreCounts.albatross += 1;
      } else if (shots === par - 2) {
        scoreCounts.eagle += 1;
      } else if (shots === par - 1) {
        scoreCounts.birdie += 1;
      } else if (shots === par) {
        scoreCounts.par += 1;
      }
    });
  }

  // Calculate total XP earned based on score counts
  const totalXpEarned =
    scoreCounts.par * xpValues.par +
    scoreCounts.birdie * xpValues.birdie +
    scoreCounts.eagle * xpValues.eagle +
    scoreCounts.albatross * xpValues.albatross +
    scoreCounts.holeInOne * xpValues.holeInOne;

  useEffect(() => {
    let frame = 0;
    const startXP = 0;
    const endXP = totalXpEarned;
    let interval;
    if (endXP > 0) {
      interval = setInterval(() => {
        frame++;
        const progress = frame / TOTAL_FRAMES;
        const easedProgress = progress * (2 - progress);
        const currentValue = Math.round(startXP + (endXP - startXP) * easedProgress);
        setAnimatedTotalXp(currentValue);
        if (frame === TOTAL_FRAMES) {
          setAnimatedTotalXp(endXP);
          clearInterval(interval);
        }
      }, 1000 / FRAME_RATE);
    } else {
      setAnimatedTotalXp(0);
    }
    return () => clearInterval(interval);
  }, [totalXpEarned]);

  if (!currentRoundData) {
    return <div>Loading round summary...</div>;
  }

  return (
    <div id="between-rounds-page" className="page">
      <h1 className='page-title'>{type === 'end-event' ? 'Event Summary' : 'Round Summary'}</h1>

      {xpData && (
        <div className="xp-progress-container">
          <h2>XP Progress</h2>
          {hasLeveledUp && (
            <div className="level-up-message">
              <p>You have reached Level {xpData.level}!</p>
            </div>
          )}
          <LevelCircle
            level={xpData.level}
            xp={xpData.xp}
            xpToNextLevel={xpData.xp_to_next_level}
          />
          <p className="xp-progress-label">
            {displayedXP} / {xpData.xp_to_next_level} XP
          </p>
        </div>
      )}

      <div className="stats-card">
        {scoreCounts.par > 0 && (
          <p>
            {scoreCounts.par} x Pars = <strong>{scoreCounts.par * xpValues.par} XP</strong>
          </p>
        )}
        {scoreCounts.birdie > 0 && (
          <p>
            {scoreCounts.birdie} x Birdies = <strong>{scoreCounts.birdie * xpValues.birdie} XP</strong>
          </p>
        )}
        {scoreCounts.eagle > 0 && (
          <p>
            {scoreCounts.eagle} x Eagles = <strong>{scoreCounts.eagle * xpValues.eagle} XP</strong>
          </p>
        )}
        {scoreCounts.albatross > 0 && (
          <p>
            {scoreCounts.albatross} x Albatrosses = <strong>{scoreCounts.albatross * xpValues.albatross} XP</strong>
          </p>
        )}
        {scoreCounts.holeInOne > 0 && (
          <p>
            {scoreCounts.holeInOne} x Holes-in-One = <strong>{scoreCounts.holeInOne * xpValues.holeInOne} XP</strong>
          </p>
        )}
        <p>
          <strong>Total XP Earned: <br /> <span id='totalXpBetween'>{animatedTotalXp} XP</span></strong>
        </p>
      </div>
        <div className="between-rounds-leaderboard">
          <h2>Current Leaderboard</h2>
          {leaderboardLoading ? (
            <div className="leaderboard-loading">Loading...</div>
          ) : (
            <table className="leaderboard-table">
          <thead>
            <tr>
              <th>Rank</th>
              <th>Username</th>
              <th>Total Shots</th>
            </tr>
          </thead>
          <tbody>
            {leaderboard.map((user, idx) => (
              <tr key={idx}>
            <td>{idx + 1}</td>
            <td>{user.username && user.username.toUpperCase()}</td>
            <td>{user.total_shots}</td>
              </tr>
            ))}
          </tbody>
            </table>
          )}
          <button className="refresh-leaderboard-btn" onClick={fetchLeaderboard} disabled={leaderboardLoading}>
            {leaderboardLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

      {nextCourse && (
        <div className="next-course-message">
          <p>
            Make your way to <strong>{nextCourse.name}</strong>!
          </p>
        </div>
      )}

      {!nextCourse && (
        <div className="next-course-message">
          <p>
            You have completed this event!
          </p>
        </div>
      )}

      {type === 'end-event' ? (
        <button onClick={() => navigate('/current-events')}>Back to Events</button>
      ) : (
        <button onClick={onStartNextRound}>Begin Next Round</button>
      )}
    </div>
  );
}

export default BetweenRounds;

